import { Box, Heading, Text, VStack, HStack, Badge, Table, Thead, Tbody, Tr, Th, Td, Alert, AlertIcon, Code } from '@chakra-ui/react';
import MarketingLayout from '../components/marketing/MarketingLayout';
import CodeBlock from '../components/marketing/CodeBlock';

const loginExample = `curl -X POST https://YOUR_DOMAIN/api/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email":"you@example.com","password":"••••••"}'

# → { "token": "eyJ...", "user": { "id", "email", "name", "role" } }`;

const authHeader = `Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6...`;

const groups = [
  {
    name: 'Tracking', note: 'Public, high-volume endpoints called by the tracking script.',
    rows: [
      ['POST', '/api/track', 'Public', 'Record a pageview, event, heartbeat, or leave signal'],
      ['GET', '/api/track/pixel.gif', 'Public', '1×1 tracking pixel for email / no-JS fallback'],
      ['GET', '/api/track/config', 'Public', 'Return the active tracking configuration'],
    ],
  },
  {
    name: 'Ad serving', note: 'Public endpoints called by serve.js and email creatives.',
    rows: [
      ['GET', '/api/ads/serve', 'Public', 'Serve the best eligible ad for a zone'],
      ['POST', '/api/ads/click-beacon', 'Public', 'Record a click via sendBeacon before navigation'],
      ['GET', '/api/ads/click', 'Public', 'Legacy click redirect (still supported)'],
      ['GET', '/api/ads/impression', 'Public', 'Record an email impression via pixel'],
      ['GET', '/api/ads/image/:adId', 'Public', 'Serve an ad image and record the impression'],
      ['GET', '/api/ads/debug/:zoneId', 'Token', 'Diagnose why a zone is not serving ads'],
    ],
  },
  {
    name: 'Analytics', note: 'Authenticated, read-only reporting. All accept site, start, end query params.',
    rows: [
      ['GET', '/api/analytics/overview', 'Token', 'Headline visitor, session, and ad metrics'],
      ['GET', '/api/analytics/traffic', 'Token', 'Time-series visitors / visits / pageviews'],
      ['GET', '/api/analytics/pages', 'Token', 'Top pages with engagement metrics'],
      ['GET', '/api/analytics/sources', 'Token', 'Traffic sources by type'],
      ['GET', '/api/analytics/devices', 'Token', 'Desktop / mobile / tablet breakdown'],
      ['GET', '/api/analytics/geo', 'Token', 'Top countries'],
      ['GET', '/api/analytics/landing-pages', 'Token', 'Top entry pages'],
      ['GET', '/api/analytics/exit-pages', 'Token', 'Top exit pages'],
      ['GET', '/api/analytics/stickiness', 'Token', 'DAU / WAU / MAU ratios'],
      ['GET', '/api/analytics/hourly-activity', 'Token', 'Activity by hour of day'],
      ['GET', '/api/analytics/search-keywords', 'Token', 'Organic search keywords'],
      ['GET', '/api/analytics/reports/overview', 'Token', 'Ad impressions / clicks / CTR summary'],
      ['GET', '/api/analytics/reports/by-channel', 'Token', 'Performance by channel (web vs email)'],
      ['GET', '/api/analytics/reports/by-advertiser', 'Token', 'Performance by advertiser'],
      ['GET', '/api/analytics/reports/by-site', 'Token', 'Performance by site'],
      ['GET', '/api/analytics/reports/by-campaign', 'Token', 'Performance by campaign'],
    ],
  },
  {
    name: 'Campaigns & Ads', note: 'Manage campaigns and their creatives. Ad create/update accept multipart uploads.',
    rows: [
      ['GET', '/api/campaigns', 'Token', 'List campaigns with stats (paginated)'],
      ['GET', '/api/campaigns/:id', 'Token', 'Get a campaign with linked zones and ads'],
      ['POST', '/api/campaigns', 'Token', 'Create a campaign'],
      ['PUT', '/api/campaigns/:id', 'Token', 'Update a campaign'],
      ['DELETE', '/api/campaigns/:id', 'Token', 'Delete a campaign'],
      ['GET', '/api/campaigns/:id/ads', 'Token', 'List ads for a campaign'],
      ['POST', '/api/campaigns/:id/ads', 'Token', 'Create an ad (multipart)'],
      ['PUT', '/api/campaigns/:id/ads/:adId', 'Token', 'Update an ad (multipart)'],
      ['POST', '/api/campaigns/:id/ads/:adId/duplicate', 'Token', 'Duplicate an ad'],
      ['DELETE', '/api/campaigns/:id/ads/:adId', 'Token', 'Delete an ad'],
    ],
  },
  {
    name: 'Zones',
    rows: [
      ['GET', '/api/zones', 'Token', 'List zones with stats (paginated)'],
      ['GET', '/api/zones/:id', 'Token', 'Get a zone with linked campaigns'],
      ['POST', '/api/zones', 'Token', 'Create a zone'],
      ['PUT', '/api/zones/:id', 'Token', 'Update a zone'],
      ['DELETE', '/api/zones/:id', 'Token', 'Delete a zone'],
      ['GET', '/api/zones/:id/code', 'Token', 'Get embed codes (website + WordPress)'],
    ],
  },
  {
    name: 'Sites',
    rows: [
      ['GET', '/api/sites', 'Token', 'List sites with visitor stats (paginated)'],
      ['GET', '/api/sites/search', 'Token', 'Search across sites, campaigns, ads, zones'],
      ['GET', '/api/sites/:id', 'Token', 'Get a single site'],
      ['POST', '/api/sites', 'Token', 'Create a site (auto-generates API key)'],
      ['PUT', '/api/sites/:id', 'Token', 'Update a site'],
      ['DELETE', '/api/sites/:id', 'Token', 'Delete a site'],
      ['POST', '/api/sites/:id/regenerate-key', 'Token', 'Regenerate the site API key'],
      ['GET', '/api/sites/:id/tracking-code', 'Token', 'Get the tracking snippet'],
      ['DELETE', '/api/sites/admin/purge-data', 'Admin', 'Purge selected data types'],
      ['DELETE', '/api/sites/admin/purge-all-data', 'Admin', 'Purge all tracking data'],
    ],
  },
  {
    name: 'Advertisers',
    rows: [
      ['GET', '/api/advertisers', 'Token', 'List advertisers with performance'],
      ['GET', '/api/advertisers/:id', 'Token', 'Get an advertiser with campaigns'],
      ['POST', '/api/advertisers', 'Token', 'Create an advertiser'],
      ['PUT', '/api/advertisers/:id', 'Token', 'Update an advertiser'],
      ['DELETE', '/api/advertisers/:id', 'Token', 'Delete an advertiser (if no campaigns)'],
    ],
  },
  {
    name: 'UTM Links',
    rows: [
      ['GET', '/api/links', 'Token', 'List trackable links with click stats'],
      ['GET', '/api/links/:id', 'Token', 'Get a link with click stats'],
      ['GET', '/api/links/:id/analytics', 'Token', 'Detailed click analytics for a link'],
      ['POST', '/api/links', 'Token', 'Create a link (auto-generates short code)'],
      ['PUT', '/api/links/:id', 'Token', 'Update a link'],
      ['DELETE', '/api/links/:id', 'Token', 'Delete a link'],
      ['GET', '/l/:shortCode', 'Public', 'Redirect and record a click'],
    ],
  },
  {
    name: 'Settings',
    rows: [
      ['GET', '/api/settings/my-ip', 'Token', 'Return the caller’s IP address'],
      ['GET', '/api/settings', 'Token', 'Get all global settings'],
      ['GET', '/api/settings/tracking', 'Token', 'Get tracking configuration'],
      ['PUT', '/api/settings/tracking', 'Admin', 'Update tracking configuration'],
      ['GET', '/api/settings/excluded-ips', 'Token', 'List excluded IPs'],
      ['PUT', '/api/settings/excluded-ips', 'Admin', 'Replace the excluded-IP list'],
      ['POST', '/api/settings/excluded-ips/add', 'Admin', 'Add an excluded IP'],
      ['DELETE', '/api/settings/excluded-ips/:ip', 'Admin', 'Remove an excluded IP'],
      ['POST', '/api/settings/excluded-ips/add-current', 'Admin', 'Exclude the caller’s IP'],
    ],
  },
  {
    name: 'Auth & Users',
    rows: [
      ['POST', '/api/auth/login', 'Public', 'Log in and receive a JWT (5/15min)'],
      ['POST', '/api/auth/register', 'Public', 'Disabled — returns 403'],
      ['GET', '/api/auth/me', 'Token', 'Get the current user'],
      ['PUT', '/api/auth/profile', 'Token', 'Update name / email'],
      ['PUT', '/api/auth/change-password', 'Token', 'Change password'],
      ['GET', '/api/auth/users', 'Admin', 'List users'],
      ['POST', '/api/auth/users', 'Admin', 'Create a user'],
      ['PUT', '/api/auth/users/:id', 'Admin', 'Update a user'],
      ['DELETE', '/api/auth/users/:id', 'Admin', 'Delete a user'],
    ],
  },
  {
    name: 'License',
    rows: [
      ['GET', '/api/license/status', 'Public', 'Check license validity (read-only mode)'],
      ['GET', '/api/license', 'Token', 'Get full license details'],
      ['POST', '/api/license/activate', 'Token', 'Activate a license key'],
      ['POST', '/api/license/validate', 'Token', 'Re-validate the license'],
    ],
  },
];

const METHOD_COLORS = { GET: 'green', POST: 'blue', PUT: 'orange', DELETE: 'red' };
const AUTH_COLORS = { Public: 'gray', Token: 'purple', Admin: 'red' };

export const ApiReferencePage = () => (
  <MarketingLayout title="API Reference" subtitle="The HTTP API behind StatFalcon. Base URL is your StatFalcon domain." maxW="1000px">
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="lg" color="gray.900" mb={3}>Authentication</Heading>
        <Text color="gray.700" lineHeight="1.8">
          Management endpoints use a JSON Web Token. POST your credentials to <Code>/api/auth/login</Code> to receive
          a token (valid 7 days), then send it on every authenticated request:
        </Text>
        <CodeBlock label="Authorization header">{authHeader}</CodeBlock>
        <CodeBlock label="curl">{loginExample}</CodeBlock>
        <HStack spacing={4} flexWrap="wrap" mt={2}>
          <HStack><Badge colorScheme="gray">Public</Badge><Text fontSize="sm" color="gray.600">no token required</Text></HStack>
          <HStack><Badge colorScheme="purple">Token</Badge><Text fontSize="sm" color="gray.600">valid JWT required</Text></HStack>
          <HStack><Badge colorScheme="red">Admin</Badge><Text fontSize="sm" color="gray.600">admin role required</Text></HStack>
        </HStack>
      </Box>

      <Alert status="warning" borderRadius="xl">
        <AlertIcon />
        Creating, updating, or deleting data requires a valid license. Without one the API runs read-only and
        mutations return <Code>403</Code>. The tracking, ad-serving, auth, and license endpoints are always available.
      </Alert>

      {groups.map((group) => (
        <Box key={group.name}>
          <Heading size="md" color="gray.900" mt={6} mb={group.note ? 1 : 3}>{group.name}</Heading>
          {group.note && <Text color="gray.500" fontSize="sm" mb={3}>{group.note}</Text>}
          <Box bg="white" borderRadius="xl" overflow="hidden" border="1px solid" borderColor="gray.200" overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Method</Th>
                  <Th>Endpoint</Th>
                  <Th>Auth</Th>
                  <Th>Description</Th>
                </Tr>
              </Thead>
              <Tbody>
                {group.rows.map(([method, path, auth, desc]) => (
                  <Tr key={`${method}-${path}`}>
                    <Td><Badge colorScheme={METHOD_COLORS[method]}>{method}</Badge></Td>
                    <Td><Code fontSize="xs" bg="transparent" color="gray.800">{path}</Code></Td>
                    <Td><Badge colorScheme={AUTH_COLORS[auth]} variant="subtle">{auth}</Badge></Td>
                    <Td color="gray.600" fontSize="sm">{desc}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      ))}
    </VStack>
  </MarketingLayout>
);

export default ApiReferencePage;
